/**
 * REV-577: a compact "how long ago" label for the tracked-place summary cards.
 *
 * Why this exists rather than a larger font: the "Last scan" card sat at body2
 * while its four siblings used h4, which read as un-uniform. Matching h4
 * directly is not possible — measured in the browser, the full timestamp
 * ("Aug 10, 2026, 10:32 AM") is 375px at h4 against a card whose grid minimum
 * is 160px, so it would wrap across about three lines and look worse than the
 * inconsistency being fixed. The four sibling values are 2-digit counts, ~35px.
 *
 * Uniformity therefore needs a SHORTER value, not a bigger font. The exact
 * timestamp is not lost — callers put it in a `title` so it is available on
 * hover and to assistive tech.
 *
 * Deliberately not Intl.RelativeTimeFormat: that yields "2 days ago" (~170px
 * at h4), still too wide for a 160px card. These abbreviations stay under the
 * width of a 2-digit count at the same size.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * @param iso    ISO timestamp, or null/undefined when the event never happened.
 * @param now    Injected so callers and tests are deterministic — a relative
 *               label read from the wall clock makes for a flaky test and a
 *               value that silently changes between renders.
 * @returns A short label: 'Never', 'Now', '5m', '3h', '2d', '4mo', '1y'.
 */
export function compactRelativeTime(
  iso: string | null | undefined,
  now: Date = new Date()
): string {
  if (!iso) return 'Never';

  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Never';

  const delta = now.getTime() - then;

  // A future timestamp is clock skew, not a scan scheduled ahead of time.
  // Reporting "Now" is honest and avoids a nonsensical "-3d".
  if (delta < MINUTE) return 'Now';
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h`;
  if (delta < MONTH) return `${Math.floor(delta / DAY)}d`;
  if (delta < YEAR) return `${Math.floor(delta / MONTH)}mo`;
  return `${Math.floor(delta / YEAR)}y`;
}
